import React, { useState, useEffect } from 'react';
import './NotesModal.css';

function NotesModal({ isOpen, onClose, notes, onUpdateNote, races, initialTarget, onOverlayClick, gameMode }) {
    const categories = React.useMemo(() => ['algemeen', ...races], [races]);
    const [selectedCategory, setSelectedCategory] = useState(() => {
        // Preference: 1. initialTarget (from trigger), 2. session storage, 3. 'algemeen'
        if (initialTarget && categories.includes(initialTarget)) return initialTarget;
        const last = sessionStorage.getItem('kopmanpuzzel_last_note_target');
        return (last && categories.includes(last)) ? last : 'algemeen';
    });

    // Update selected category if initialTarget changes (e.g. clicking a different race icon)
    useEffect(() => {
        if (initialTarget && categories.includes(initialTarget)) {
            setSelectedCategory(initialTarget);
        }
    }, [initialTarget, categories]); // categories dependency to ensure logic is safe

    // Save selection to session
    useEffect(() => {
        sessionStorage.setItem('kopmanpuzzel_last_note_target', selectedCategory);
    }, [selectedCategory]);

    if (!isOpen) return null;

    const currentText = notes[selectedCategory] || '';
    const maxWords = (selectedCategory === 'algemeen' || selectedCategory.toLowerCase() === 'eindklassement') ? 1000 : 500;

    const handleTextChange = (e) => {
        const text = e.target.value;
        const words = text.trim().split(/\s+/).filter(w => w.length > 0);
        
        if (words.length <= maxWords) {
            onUpdateNote(selectedCategory, text);
        } else {
            // If already at limit, only allow deleting or modifying without adding words
            const oldWords = currentText.trim().split(/\s+/).filter(w => w.length > 0);
            if (words.length <= oldWords.length) {
                onUpdateNote(selectedCategory, text);
            }
        }
    };

    const countWords = (text) => {
        return text.trim().split(/\s+/).filter(w => w.length > 0).length;
    };

    const wordCount = countWords(currentText);

    const handleNext = () => {
        const currentIndex = categories.indexOf(selectedCategory);
        const nextIndex = (currentIndex + 1) % categories.length;
        setSelectedCategory(categories[nextIndex]);
    };

    const handlePrev = () => {
        const currentIndex = categories.indexOf(selectedCategory);
        const prevIndex = (currentIndex - 1 + categories.length) % categories.length;
        setSelectedCategory(categories[prevIndex]);
    };

    return (
        <div className="notes-modal-overlay" onClick={onOverlayClick || onClose}>
            <div className="notes-modal-content modal-box" onClick={e => e.stopPropagation()}>
                <div className="notes-modal-header">
                    <div className="notes-header-title">
                        <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" className="notes-icon-svg">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                        </svg>
                        <h3>Notities</h3>
                    </div>
                    <button className="notes-close-btn" onClick={(e) => { 
                        e.preventDefault(); 
                        e.stopPropagation(); 
                        setTimeout(onClose, 50); 
                    }}>✕</button>
                </div>

                <div className="notes-modal-body">
                    <div className="notes-controls">
                        <button className="nav-arrow-btn" onClick={handlePrev} title="Vorige">
                            <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="15 18 9 12 15 6"></polyline>
                            </svg>
                        </button>
                        
                        <select 
                            className="notes-category-select" 
                            value={selectedCategory} 
                            onChange={(e) => setSelectedCategory(e.target.value)}
                        >
                            <option value="algemeen">Algemene notities</option>
                            {races.map(race => (
                                <option key={race} value={race}>{race}</option>
                            ))}
                        </select>

                        <button className="nav-arrow-btn" onClick={handleNext} title="Volgende">
                            <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="9 18 15 12 9 6"></polyline>
                            </svg>
                        </button>
                    </div>

                    {gameMode !== 'klassiekers' && selectedCategory !== 'algemeen' && (
                        <div className="description-field-container">
                            <input
                                type="text"
                                className="notes-description-input"
                                placeholder="Voer hier kort de omschrijving van de etappe in"
                                value={notes[`desc_${selectedCategory}`] || ''}
                                onChange={(e) => onUpdateNote(`desc_${selectedCategory}`, e.target.value)}
                                maxLength={100}
                            />
                            <div className="header-char-counter">
                                {(notes[`desc_${selectedCategory}`] || '').length} / 100
                            </div>
                        </div>
                    )}

                    <div className="textarea-container">
                        <textarea
                            className="notes-textarea"
                            placeholder={`Typ hier je notities voor ${selectedCategory === 'algemeen' ? 'het hele spel' : selectedCategory}...`}
                            value={currentText}
                            onChange={handleTextChange}
                            maxLength={5000}
                        />
                        <div className={`word-counter ${wordCount >= (maxWords - 50) ? 'near-limit' : ''}`}>
                            {wordCount} / {maxWords} woorden
                        </div>
                    </div>
                </div>

                <div className="notes-modal-footer">
                    <div className="auto-save-status">
                        <span className="auto-save-dot"></span>
                        Automatisch opgeslagen
                    </div>
                    <button className="notes-footer-close-btn" onClick={onClose}>Sluiten</button>
                </div>
            </div>
        </div>
    );
}

export default NotesModal;
